import { Box, Flex, Skeleton } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { Funding } from '@finley-technologies/finley-types';

import { DateInput, InputContainer, TextInput, SelectInput } from './Inputs';
import { EditLoanFormFields } from './types';
import { useEditLoanContext } from './useEditLoanContext';
import { SelectOption } from '~/packages/common-frontend/components/Select';
import { Typography } from '~/packages/common-frontend/components/Typography';

type LoanMetadataEditFormProps = {
  readOnly?: boolean;
};

export const LoanMetadataEditForm = ({
  readOnly = false,
}: LoanMetadataEditFormProps) => {
  const context = useEditLoanContext();

  const pricingOptions = useMemo<SelectOption[]>(() => {
    if (context.status !== 'ready') {
      return [];
    }

    const { newEntities, originalResolvedLoan } = context.state;
    if (newEntities) {
      return newEntities.newResolvedLoan.facility.loanMechanics.pricingOptions.map(
        (pricingOption: Funding.PricingOption) => ({
          label: pricingOption.name,
          value: pricingOption.key,
        }),
      );
    }

    return originalResolvedLoan.facility.loanMechanics.pricingOptions.map(
      (pricingOption: Funding.PricingOption) => ({
        label: pricingOption.name,
        value: pricingOption.key,
      }),
    );
  }, [context.status, context.state]);

  if (context.status !== 'ready') {
    // TODO: add loading state
    return <Skeleton w="100%" h="10rem" />;
  }

  return (
    <Box gap="1rem" w="100%">
      <Typography variant="section-title">Loan Details</Typography>
      <Flex
        width="100%"
        gap="1rem"
        alignItems="flex-start"
        direction="column"
        borderRadius=".5rem"
        borderWidth="1px"
        borderColor="gray.100"
        padding="2.5rem"
      >
        <InputContainer label="External Loan ID">
          <TextInput<EditLoanFormFields>
            name="externalLoanId"
            helperText="Optional"
            readOnly={readOnly}
          />
        </InputContainer>
        <InputContainer label="Pricing Option">
          <Box w="100%">
            <SelectInput
              name="pricingOptionKey"
              options={pricingOptions}
              // Pricing option shouldn't be editable, as that basically creates
              // a completely different type of loan. User should just create a
              // new loan if they need this type of action
              isDisabled
            />
          </Box>
        </InputContainer>
        <InputContainer label="Maturity Date">
          <Box w="100%">
            <DateInput name="maturityDate" readOnly={readOnly} />
          </Box>
        </InputContainer>
      </Flex>
    </Box>
  );
};
